import {
  Currency,
  SellerAccountStatus,
  StripeAccountDetailsQuery,
} from "@/Apollo/schema";
import { formatAccountMoney } from "@/common/utils/account-format.utils";
import { formatDateWithLocale } from "@/common/utils/intl.utils";
import { StripeBalance } from "@/components/Account/payment/MoneyBreakdown";
import { DeadlinePanel } from "@/components/Account/payment/StatusCard";
import { TaskItem } from "@/components/Account/payment/TaskChecklist";
import {
  DEADLINE_URGENT_DAYS,
  PaymentCopyKey,
  REVIEW_CONTACT_BUSINESS_DAYS,
  REVIEW_SINCE_KNOWN_AFTER_MS,
  REVIEW_SLOW_BUSINESS_DAYS,
} from "@/components/Account/payment/payment.static";
import {
  amountLineKey,
  businessDaysSince,
  calendarDaysUntil,
  describeUpdated,
  documentReasonLineKeys,
} from "@/components/Account/payment/payment.utils";
import { VerificationCtaGroup } from "@/components/Account/stripeVerification.utils";
import { toContentLocale } from "@/i18n/config";

export type StripeDetails = NonNullable<
  StripeAccountDetailsQuery["stripeAccountDetails"]
>;

/** The copy renderer the page hands in (usePaymentCopy's `copy`). */
export type Copy = (
  copyKey: PaymentCopyKey,
  values?: Record<string, string | number>
) => string;

type Locale = string | undefined;

/** dayjs registers the six content languages only: `en-us` reads `en`. */
const dayjsLocale = (locale: Locale) => toContentLocale(locale ?? "nl");

/** The currency every amount on the page is shown in. */
export const detailsCurrency = (details: StripeDetails): Currency =>
  details.amount_waiting?.currency ??
  details.balance?.available.currency ??
  details.bank_account?.currency ??
  Currency.Eur;

/** "€1,542.50 is waiting for you", only where money is the subject and above zero. */
export const amountLine = (
  details: StripeDetails,
  status: SellerAccountStatus,
  copy: Copy,
  locale: Locale
): string | null => {
  const copyKey = amountLineKey(status);
  const waiting = details.amount_waiting;

  if (!copyKey || !waiting || waiting.amount <= 0) {
    return null;
  }

  return copy(copyKey, {
    amount: formatAccountMoney(waiting.amount, {
      locale,
      currency: detailsCurrency(details),
      place: "accounting",
    }),
  });
};

/** A future deadline while payouts still run; once passed, the title says it all. */
export const deadlinePanel = (
  details: StripeDetails,
  status: SellerAccountStatus,
  now: Date,
  locale: Locale
): DeadlinePanel | null => {
  if (status !== SellerAccountStatus.ActiveActionNeeded || !details.deadline) {
    return null;
  }

  const deadline = new Date(details.deadline);

  if (deadline <= now) {
    return null;
  }

  return {
    date: formatDateWithLocale(
      details.deadline,
      "D MMMM YYYY",
      dayjsLocale(locale)
    ),
    urgent: calendarDaysUntil(deadline, now) <= DEADLINE_URGENT_DAYS,
  };
};

/** A review running long gets the panel; past a week it links customer service. */
export const slowReviewPanel = (
  details: StripeDetails,
  status: SellerAccountStatus,
  now: Date
): { contact: boolean } | null => {
  if (status !== SellerAccountStatus.UnderReview || !details.pending_since) {
    return null;
  }

  const days = businessDaysSince(new Date(details.pending_since), now);

  return days >= REVIEW_SLOW_BUSINESS_DAYS
    ? { contact: days >= REVIEW_CONTACT_BUSINESS_DAYS }
    : null;
};

/**
 * A fresh status says nothing about its age. Past the gateway's refresh
 * window the re-read on open failed (or is not deployed yet): how old it is.
 */
export const staleLabel = (
  details: StripeDetails,
  now: Date,
  copy: Copy,
  locale: Locale
): string | null => {
  const updated = describeUpdated(details.verification_as_of, now);

  if (!updated || updated.copyKey === "updatedJustNow") {
    return null;
  }

  return updated.copyKey === "updatedMinutesAgo"
    ? copy("updatedMinutesAgo", { minutes: updated.minutes })
    : copy("updatedOn", {
        date: formatDateWithLocale(
          updated.date,
          "D MMM YYYY",
          dayjsLocale(locale)
        ),
      });
};

/** Before onboarding: what Stripe will ask for, so the seller knows what to have ready. */
export const expectationTasks = (copy: Copy): TaskItem[] => [
  {
    id: "details",
    title: copy("expectDetailsTitle"),
    description: null,
    why: copy("expectDetailsWhy"),
    reasonLines: [],
  },
  {
    id: "identity-document",
    title: copy("expectIdTitle"),
    description: null,
    why: copy("expectIdWhy"),
    reasonLines: [],
  },
];

/** The open Stripe tasks as checklist rows, with plain-words reason lines for a rejected document. */
export const issueTasks = (
  groups: VerificationCtaGroup[],
  copy: Copy
): TaskItem[] =>
  groups
    .filter(group => group.cta.kind === "stripe-link")
    .flatMap(group =>
      group.issues.map(issue => ({
        id: issue.code,
        title: issue.title,
        description: issue.description,
        why: issue.why,
        reasonLines: documentReasonLineKeys(issue.reasonCodes).map(reasonKey =>
          copy(reasonKey)
        ),
      }))
    );

/** True when the review demonstrably predates the mirror read that carries it. */
export const reviewSinceIsKnown = (details: StripeDetails): boolean => {
  if (!details.pending_since || !details.verification_as_of) {
    return false;
  }

  return (
    new Date(details.verification_as_of).getTime() -
      new Date(details.pending_since).getTime() >=
    REVIEW_SINCE_KNOWN_AFTER_MS
  );
};

/** What Stripe is reviewing, dated only when the classification knows since when. */
export const reviewTasks = (
  details: StripeDetails,
  status: SellerAccountStatus,
  copy: Copy,
  locale: Locale
): TaskItem[] => {
  if (status !== SellerAccountStatus.UnderReview) {
    return [];
  }

  // The classifier stamps pending_since when it first sees the review, so
  // a stamp from the same read as the status says nothing about when Stripe
  // started. Only an older stamp earns a date, and then as "since", never
  // as "submitted on".
  if (details.pending_since && reviewSinceIsKnown(details)) {
    return [
      {
        id: "review-document",
        title: copy("reviewDocumentTitle"),
        description: copy("reviewSince", {
          date: formatDateWithLocale(
            details.pending_since,
            "D MMMM",
            dayjsLocale(locale)
          ),
        }),
        why: null,
        reasonLines: [],
      },
    ];
  }

  return [
    {
      id: "review-details",
      title: copy("reviewDetailsTitle"),
      description: copy("reviewInProgress"),
      why: null,
      reasonLines: [],
    },
  ];
};

export const sellerBalance = (details: StripeDetails): StripeBalance | null =>
  details.balance
    ? {
        available: details.balance.available.amount,
        pending: details.balance.pending.amount,
        currency: details.balance.available.currency,
      }
    : null;
